import type { ColorTable, ExtractedColor } from "./types"
import { generateId } from "./svg-color-extractor"

/**
 * Interface para salvar uma tabela de cores no banco de dados
 * Esta função deve ser implementada de acordo com o banco de dados utilizado
 */
export interface SaveColorTableParams {
  name: string
  description?: string
  colors: ExtractedColor[]
  svgContent?: string
}

/**
 * Função para preparar os dados da tabela de cores para salvar no banco
 * Retorna o objeto pronto para ser inserido no banco de dados
 *
 * @example
 * // Uso com Prisma
 * const data = prepareColorTableForSave({ name, description, colors, svgContent })
 * await prisma.colorTable.create({ data })
 *
 * @example
 * // Uso com Supabase
 * const data = prepareColorTableForSave({ name, description, colors, svgContent })
 * await supabase.from('color_tables').insert(data)
 *
 * @example
 * // Uso com MongoDB
 * const data = prepareColorTableForSave({ name, description, colors, svgContent })
 * await collection.insertOne(data)
 */
export function prepareColorTableForSave(params: SaveColorTableParams): ColorTable {
  const now = new Date()

  return {
    id: generateId(),
    name: params.name,
    description: params.description,
    colors: params.colors,
    svgContent: params.svgContent,
    createdAt: now,
    updatedAt: now,
  }
}

/**
 * Exemplo de função para salvar no banco de dados
 * Adapte esta função conforme o banco de dados que você está utilizando
 *
 * @example
 * // Implementação com Prisma
 * export async function saveColorTableToDB(params: SaveColorTableParams): Promise<ColorTable> {
 *   const data = prepareColorTableForSave(params)
 *   return await prisma.colorTable.create({
 *     data: {
 *       id: data.id,
 *       name: data.name,
 *       description: data.description,
 *       colors: data.colors,
 *       svgContent: data.svgContent,
 *       createdAt: data.createdAt,
 *       updatedAt: data.updatedAt,
 *     }
 *   })
 * }
 *
 * @example
 * // Implementação com Supabase
 * export async function saveColorTableToDB(params: SaveColorTableParams): Promise<ColorTable> {
 *   const data = prepareColorTableForSave(params)
 *   const { data: result, error } = await supabase
 *     .from('color_tables')
 *     .insert({
 *       id: data.id,
 *       name: data.name,
 *       description: data.description,
 *       colors: JSON.stringify(data.colors),
 *       svg_content: data.svgContent,
 *       created_at: data.createdAt.toISOString(),
 *       updated_at: data.updatedAt.toISOString(),
 *     })
 *     .select()
 *     .single()
 *
 *   if (error) throw error
 *   return result
 * }
 */

// SQL para criar a tabela (PostgreSQL/Supabase)
export const CREATE_TABLE_SQL = `
CREATE TABLE IF NOT EXISTS color_tables (
  id TEXT PRIMARY KEY,
  name TEXT NOT NULL,
  description TEXT,
  colors JSONB NOT NULL,
  svg_content TEXT,
  created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
  updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
);

-- Índice para busca por nome
CREATE INDEX IF NOT EXISTS idx_color_tables_name ON color_tables(name);

-- Trigger para atualizar updated_at automaticamente
CREATE OR REPLACE FUNCTION update_updated_at_column()
RETURNS TRIGGER AS $$
BEGIN
  NEW.updated_at = NOW();
  RETURN NEW;
END;
$$ language 'plpgsql';

DROP TRIGGER IF EXISTS update_color_tables_updated_at ON color_tables;
CREATE TRIGGER update_color_tables_updated_at
  BEFORE UPDATE ON color_tables
  FOR EACH ROW
  EXECUTE FUNCTION update_updated_at_column();
`

// SQL para Prisma schema
export const PRISMA_SCHEMA = `
model ColorTable {
  id          String   @id
  name        String
  description String?
  colors      Json
  svgContent  String?  @map("svg_content")
  createdAt   DateTime @default(now()) @map("created_at")
  updatedAt   DateTime @updatedAt @map("updated_at")

  @@map("color_tables")
}
`
